import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Sparkles, Users, Crown, Lock, Info, Tag, Clock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePaymentsVisibility } from '../hooks/usePaymentsVisibility';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RECRUITER_DEMO_URL } from '../constants/recruiterContact';

const LAUNCH_OFFER_DEADLINE = new Date('2026-04-26T23:59:59');

function useCountdown(deadline: Date) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = deadline.getTime() - now.getTime();
  if (diff <= 0) return null;

  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

interface PlanFeature {
  name: string;
  explorer_free: boolean | string;
  explorer_premium: boolean | string;
  recruiter_free: boolean | string;
  recruiter_premium: boolean | string;
  info?: string;
}

const Pricing: React.FC = () => {
  const { user, apiCall, isSignedIn } = useAuth();
  const showPayments = usePaymentsVisibility();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('pricing');
  const [loading, setLoading] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('explorer_free');
  const [isRecruiter, setIsRecruiter] = useState(false);
  const [tableView, setTableView] = useState<'explorer' | 'recruiter'>('explorer');
  const { userProfile } = useUserProfile();
  const countdown = useCountdown(LAUNCH_OFFER_DEADLINE);
  const isOfferActive = countdown !== null;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load user profile to get current plan
  useEffect(() => {
    const loadProfile = async () => {
      if (!isSignedIn || !user) return;
      
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);
        
        if (response.ok) {
          const profile = await response.json();
          setCurrentPlan(profile.subscription_type || 'explorer_free');
          const recruiter = profile.is_recruiter || false;
          setIsRecruiter(recruiter);
          if (recruiter) setTableView('recruiter');
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user?.userId]);



  // Feature comparison: split for Explorer vs Recruiter tables
  const explorerFeatures: PlanFeature[] = [
    // Mock simulations sit at the top of the comparison table -- they're
    // the newest consumer feature and the strongest hook for premium
    // upgrades, so we want them to be the first thing every visitor sees
    // when scanning what each plan includes.
    { name: t('features.mockSimulations'), explorer_free: false, explorer_premium: '10/month', recruiter_free: false, recruiter_premium: false, info: t('features.mockSimulationsInfo') },
    { name: t('features.cvSearch'), explorer_free: '30/month', explorer_premium: '300/month', recruiter_free: false, recruiter_premium: false, info: t('features.cvSearchInfo') },
    { name: t('features.reachOuts'), explorer_free: '3/month', explorer_premium: '30/month', recruiter_free: '3/month', recruiter_premium: '30/month', info: t('features.reachOutsInfo') },
    { name: t('features.cvUploads'), explorer_free: '5/month', explorer_premium: '10/month', recruiter_free: false, recruiter_premium: false, info: t('features.cvUploadsInfo') }
  ];
  // Recruiter comparison. `recruiter_free` column = Community-based Hiring
  // (Sourcing), `recruiter_premium` column = Screening.
  // Deliberately high-level for a demo-led model: we group capabilities into
  // value buckets instead of a granular checklist (no quotas, no vendor names)
  // so the page sells outcomes and doesn't hand competitors a blueprint.
  const recruiterFeatures: PlanFeature[] = [
    { name: t('features.grpSourcing'), explorer_free: false, explorer_premium: false, recruiter_free: true, recruiter_premium: true, info: t('features.grpSourcingInfo') },
    { name: t('features.grpOutreach'), explorer_free: false, explorer_premium: false, recruiter_free: true, recruiter_premium: true, info: t('features.grpOutreachInfo') },
    { name: t('features.grpAtsSync'), explorer_free: false, explorer_premium: false, recruiter_free: true, recruiter_premium: true, info: t('features.grpAtsSyncInfo') },
    { name: t('features.grpScreening'), explorer_free: false, explorer_premium: false, recruiter_free: false, recruiter_premium: true, info: t('features.grpScreeningInfo') },
    { name: t('features.grpInterviews'), explorer_free: false, explorer_premium: false, recruiter_free: false, recruiter_premium: true, info: t('features.grpInterviewsInfo') }
  ];

  // Plan pricing (monthly only for now)
  const pricing = {
    explorer_premium: 4.99,
    recruiter_premium: 99.99
  };

  const handleSubscribe = async (planType: 'explorer_premium' | 'recruiter_premium') => {
    if (!isSignedIn) {
      // Recruiter accounts are invite-only: route prospects to demo booking.
      if (planType === 'recruiter_premium') {
        window.open(RECRUITER_DEMO_URL, '_blank', 'noopener,noreferrer');
      } else {
        navigate('/signup?return=/pricing');
      }
      return;
    }

    // Prevent double subscriptions
    const hasWrongPremiumPlan = (currentPlan === 'explorer_premium' && planType === 'recruiter_premium') || 
                                 (currentPlan === 'recruiter_premium' && planType === 'explorer_premium');
    
    if (hasWrongPremiumPlan) {
      alert(t('alerts.alreadyPremium'));
      return;
    }

    setLoading(planType);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language || 'en',
        },
        body: JSON.stringify({
          plan_type: planType,
          billing_cycle: 'monthly'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.client_secret) {
        navigate('/checkout', { state: { clientSecret: data.client_secret, planType } });
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert(error.message || t('alerts.checkoutError'));
    } finally {
      setLoading(null);
    }
  };

  const canSubscribe = showPayments;

  // Helper functions for button states
  const isCurrentPlan = (planType: string) => currentPlan === planType;

  const getButtonText = (planType: 'explorer_premium' | 'recruiter_premium') => {
    if (!canSubscribe) return t('buttons.comingSoon') || 'Coming Soon';
    if (loading === planType) return t('buttons.loading') || 'Loading...';
    return t('buttons.subscribe') || 'Subscribe';
  };

  const isButtonDisabled = (planType: 'explorer_premium' | 'recruiter_premium') => {
    if (!canSubscribe) return true;
    if (loading === planType) return true;
    if (isCurrentPlan(planType)) return true;
    if (planType === 'explorer_premium' && isRecruiter && isSignedIn) return true;
    if (planType === 'recruiter_premium' && !isRecruiter && isSignedIn) return true;
    return false;
  };

  const handleManageBilling = async () => {
    if (!isSignedIn) {
      navigate('/signup');
      return;
    }

    // Navigate to custom billing page
    navigate('/settings/billing');
  };

  const renderFeatureValue = (value: boolean | string) => {
    if (value === true) {
      return <Check className="w-5 h-5 text-green-500 mx-auto" />;
    } else if (value === false) {
      return <X className="w-5 h-5 text-gray-300 mx-auto" />;
    } else {
      return <span className="text-sm font-semibold text-gray-700">{value}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-pink-50/20 py-6 sm:py-16 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-14"
        >
          {/* Promo Code Nudge + Countdown */}
          {isOfferActive && (
            <div className="flex flex-col items-center gap-2.5 sm:gap-3 mb-4 sm:mb-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="inline-flex items-center gap-2 bg-white border border-pink-200/80 rounded-full px-5 py-2.5 sm:px-6 sm:py-3 shadow-[0_2px_12px_rgba(236,72,153,0.08)]"
              >
                <Tag className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-pink-500 flex-shrink-0" />
                <span className="text-xs sm:text-sm text-gray-700">
                  {t('promo.text')}{' '}
                  <span className="font-bold text-pink-600 bg-pink-100 px-1.5 py-0.5 rounded font-mono tracking-wide">
                    {t('promo.code')}
                  </span>{' '}
                  {t('promo.textEnd')}
                </span>
                <span className="text-[10px] sm:text-xs font-bold text-white bg-pink-500 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {t('promo.badge')}
                </span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="flex items-center gap-2 text-gray-500 text-[11px] sm:text-xs"
              >
                <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span>{t('countdown.endsIn')}</span>
                <div className="flex items-center gap-1 font-mono font-semibold text-gray-800">
                  <span className="bg-gray-900 text-white px-1.5 py-0.5 rounded text-[11px] sm:text-xs min-w-[28px] text-center">
                    {countdown!.days}{t('countdown.days')}
                  </span>
                  <span className="text-gray-300">:</span>
                  <span className="bg-gray-900 text-white px-1.5 py-0.5 rounded text-[11px] sm:text-xs min-w-[28px] text-center">
                    {String(countdown!.hours).padStart(2, '0')}{t('countdown.hours')}
                  </span>
                  <span className="text-gray-300">:</span>
                  <span className="bg-gray-900 text-white px-1.5 py-0.5 rounded text-[11px] sm:text-xs min-w-[28px] text-center">
                    {String(countdown!.minutes).padStart(2, '0')}{t('countdown.minutes')}
                  </span>
                  <span className="text-gray-300">:</span>
                  <span className="bg-gray-900 text-white px-1.5 py-0.5 rounded text-[11px] sm:text-xs min-w-[28px] text-center">
                    {String(countdown!.seconds).padStart(2, '0')}{t('countdown.seconds')}
                  </span>
                </div>
              </motion.div>
            </div>
          )}

          <h1 className="text-2xl sm:text-3xl md:text-[2.1rem] font-bold text-slate-900 mb-3 sm:mb-4 tracking-tight">
            {t('header.title')}{' '}
            <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
              {t('header.titleHighlight')}
            </span>{' '}
            {t('header.titleEnd')}
          </h1>
          <p className="text-sm sm:text-lg text-slate-500 max-w-2xl mx-auto">
            {t('header.subtitle')}
          </p>

        </motion.div>

        {/* Plans Container with conditional ordering */}
        <div className="flex flex-col">
        {/* EXPLORER PLANS */}
        <div className={`mb-8 sm:mb-16 ${isRecruiter && isSignedIn ? 'order-2' : 'order-1'}`}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                {t('plans.explorerTitle')}
              </h2>
            </div>
            <span className="text-[11px] sm:text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full font-semibold border border-slate-200">
              {t('plans.explorerSubtitle')}
            </span>
          </div>

          <div 
            className={`relative ${isRecruiter && isSignedIn ? 'opacity-60' : 'opacity-100'}`}
          >
            {isRecruiter && isSignedIn && (
              <div className="absolute inset-0 bg-gray-900/5 backdrop-blur-[1px] rounded-3xl flex items-center justify-center z-10 pointer-events-none">
                <div className="bg-white px-6 py-3 rounded-full shadow-lg border-2 border-gray-300">
                  <span className="text-gray-700 font-semibold flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    {t('plans.explorerOnly')}
                  </span>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* Explorer Free */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className={`bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] p-5 sm:p-8 border flex flex-col ${
                  currentPlan === 'explorer_free' ? 'border-slate-300 ring-1 ring-slate-200' : 'border-slate-200'
                }`}
              >
                <div className="text-center mb-5 sm:mb-6 mt-2">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">Explorer Free</h3>
                  <div className="text-4xl sm:text-5xl font-bold text-slate-900 mb-1 tracking-tight">€0</div>
                  <p className="text-slate-400 text-sm">{t('cards.noPayment')}</p>
                </div>

                <div className="space-y-2.5 mb-6 sm:mb-8 flex-grow">
                  {[t('explorerFree.cvSearches'), t('explorerFree.reachOuts'), t('explorerFree.cvUploads'), t('explorerFree.prescreens')].map((feat, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-slate-500" />
                      </div>
                      <span className="text-slate-600 text-[13px] sm:text-sm">{feat}</span>
                    </div>
                  ))}
                </div>

                {currentPlan === 'explorer_free' && isSignedIn ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl py-3 text-center">
                    <span className="text-slate-600 font-semibold text-sm flex items-center justify-center gap-1.5">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {t('cards.currentPlan')}
                    </span>
                  </div>
                ) : !isSignedIn && (
                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full bg-white hover:bg-slate-50 text-slate-700 font-semibold py-3 px-6 rounded-xl border border-slate-300 hover:border-pink-300 transition-all text-sm"
                  >
                    {t('cards.getStartedFree')}
                  </button>
                )}
              </motion.div>

              {/* Explorer Premium */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className={`bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] p-5 sm:p-8 border relative ${
                  currentPlan === 'explorer_premium' ? 'border-pink-300 ring-1 ring-pink-100' : 'border-slate-200'
                }`}
              >
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-1 rounded-full text-[11px] font-bold shadow-md shadow-pink-200/50 uppercase tracking-wider">
                    {t('plans.launchOffer')}
                  </span>
                </div>

                {isCurrentPlan('explorer_premium') && (
                  <div className="absolute top-3 right-3">
                    <span className="bg-emerald-500 text-white px-2.5 py-0.5 rounded-full text-[10px] font-bold shadow-sm flex items-center gap-1">
                      <Check className="w-2.5 h-2.5" />
                      {i18n.language === 'de' ? 'Dein Plan' : 'Your Plan'}
                    </span>
                  </div>
                )}

                <div className="text-center mb-5 sm:mb-6 mt-4">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">Explorer Premium</h3>
                  <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent mb-1 tracking-tight">
                    €{pricing.explorer_premium}
                  </div>
                  <p className="text-slate-400 text-sm">{t('cards.perMonth')}</p>
                </div>

                <div className="space-y-2.5 mb-6 sm:mb-8">
                  {[t('explorerPremium.mockSimulations'), t('explorerPremium.cvSearches'), t('explorerPremium.reachOuts'), t('explorerPremium.cvUploads'), t('explorerPremium.prescreens')].map((feat, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-pink-50 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-pink-500" />
                      </div>
                      <span className="text-slate-700 text-[13px] sm:text-sm font-medium">{feat}</span>
                    </div>
                  ))}
                </div>

                {currentPlan === 'explorer_premium' ? (
                  <button
                    onClick={handleManageBilling}
                    className="w-full bg-white hover:bg-slate-50 text-slate-700 font-semibold py-3 px-6 rounded-xl border border-slate-300 hover:border-pink-300 transition-all text-sm"
                  >
                    {t('cards.manageBilling')}
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubscribe('explorer_premium')}
                    disabled={isButtonDisabled('explorer_premium')}
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold py-3.5 px-6 rounded-xl shadow-md shadow-pink-200/40 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                  >
                    {loading === 'explorer_premium' ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        {t('cards.processing')}
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4" />
                        {getButtonText('explorer_premium')}
                      </>
                    )}
                  </button>
                )}
              </motion.div>
            </div>
          </div>
        </div>

        {/* RECRUITER PLANS */}
        <div className={`mb-8 sm:mb-16 ${isRecruiter && isSignedIn ? 'order-1' : 'order-2'}`}>
          <div className="flex flex-col items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-sm shadow-pink-200/50">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                  {t('plans.recruiterTitle')}
                </h2>
              </div>
              <span className="text-[11px] sm:text-xs bg-pink-50 text-pink-600 px-3 py-1 rounded-full font-semibold border border-pink-200">
                {t('plans.recruiterSubtitle')}
              </span>
            </div>
          </div>

          <div 
            className={`relative ${!isRecruiter && isSignedIn ? 'opacity-60' : 'opacity-100'}`}
          >
            {!isRecruiter && isSignedIn && (
              <div className="absolute inset-0 bg-gray-900/5 backdrop-blur-[1px] rounded-3xl flex items-center justify-center z-10 pointer-events-none">
                <div className="bg-white px-6 py-3 rounded-full shadow-lg border-2 border-gray-300">
                  <span className="text-gray-700 font-semibold flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    {t('plans.recruiterOnly')}
                  </span>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* Community-based Hiring / Sourcing (self-serve, 10% commission) */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className={`bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] p-5 sm:p-8 border relative flex flex-col ${
                  currentPlan === 'recruiter_free' ? 'border-slate-300 ring-1 ring-slate-200' : 'border-slate-200'
                }`}
              >
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-1 rounded-full text-[11px] font-bold shadow-md shadow-pink-200/50 uppercase tracking-wider">
                    {t('recruiterTiers.sourcing.badge')}
                  </span>
                </div>

                <div className="text-center mb-5 sm:mb-6 mt-4">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">{t('recruiterTiers.sourcing.title')}</h3>
                  <div className="min-h-[2.25rem] sm:min-h-[2.75rem] flex items-center justify-center text-3xl sm:text-4xl font-bold text-slate-900 mb-1 tracking-tight">{t('recruiterTiers.sourcing.price')}</div>
                  <p className="text-slate-400 text-sm">{t('cards.perMonth')}</p>
                  <p className="text-slate-400 text-sm mt-0.5">{t('recruiterTiers.sourcing.priceNote')}</p>
                </div>

                <div className="space-y-2.5 mb-6 sm:mb-8 flex-grow">
                  {[t('recruiterTiers.sourcing.f1'), t('recruiterTiers.sourcing.f2'), t('recruiterTiers.sourcing.f3'), t('recruiterTiers.sourcing.f4'), t('recruiterTiers.sourcing.f5'), t('recruiterTiers.sourcing.f6')].map((feat, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-slate-500" />
                      </div>
                      <span className="text-slate-600 text-[13px] sm:text-sm">{feat}</span>
                    </div>
                  ))}
                </div>

                {currentPlan === 'recruiter_free' && isSignedIn ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl py-3 text-center">
                    <span className="text-slate-600 font-semibold text-sm flex items-center justify-center gap-1.5">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {t('cards.currentPlan')}
                    </span>
                  </div>
                ) : (
                  <a
                    href={RECRUITER_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-white hover:bg-slate-50 text-slate-700 font-semibold py-3 px-6 rounded-xl border border-slate-300 hover:border-pink-300 transition-all text-sm text-center"
                  >
                    {t('common:recruiterCta.bookDemo')}
                  </a>
                )}
              </motion.div>

              {/* Screening: sales-led; reduced 5% commission on Cevee-sourced hires */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className={`bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] p-5 sm:p-8 border relative flex flex-col ${
                  currentPlan === 'recruiter_star' ? 'border-pink-300 ring-1 ring-pink-100' : 'border-slate-200'
                }`}
              >
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-1 rounded-full text-[11px] font-bold shadow-md shadow-pink-200/50 uppercase tracking-wider">
                    {t('recruiterTiers.screening.badge')}
                  </span>
                </div>

                {currentPlan === 'recruiter_star' && (
                  <div className="absolute top-3 right-3">
                    <span className="bg-emerald-500 text-white px-2.5 py-0.5 rounded-full text-[10px] font-bold shadow-sm flex items-center gap-1">
                      <Check className="w-2.5 h-2.5" />
                      {i18n.language === 'de' ? 'Dein Plan' : 'Your Plan'}
                    </span>
                  </div>
                )}

                <div className="text-center mb-5 sm:mb-6 mt-4">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">{t('recruiterTiers.screening.title')}</h3>
                  <div className="min-h-[2.25rem] sm:min-h-[2.75rem] flex items-center justify-center text-xl sm:text-2xl font-bold text-slate-900 mb-1 tracking-tight">
                    {t('recruiterTiers.screening.price')}
                  </div>
                  <p className="text-slate-400 text-sm">{t('recruiterTiers.screening.priceSubtitle')}</p>
                  <p className="text-slate-400 text-sm mt-0.5">{t('recruiterTiers.screening.priceNote')}</p>
                </div>

                <div className="space-y-2.5 mb-6 sm:mb-8 flex-grow">
                  {[t('recruiterTiers.screening.f1'), t('recruiterTiers.screening.f2'), t('recruiterTiers.screening.f3'), t('recruiterTiers.screening.f4'), t('recruiterTiers.screening.f5'), t('recruiterTiers.screening.f6')].map((feat, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-pink-50 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-pink-500" />
                      </div>
                      <span className="text-slate-700 text-[13px] sm:text-sm font-medium">{feat}</span>
                    </div>
                  ))}
                </div>

                {currentPlan === 'recruiter_star' && isSignedIn ? (
                  <div className="bg-pink-50 border border-pink-200 rounded-xl py-3 text-center">
                    <span className="text-pink-700 font-semibold text-sm flex items-center justify-center gap-1.5">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {t('cards.currentPlan')}
                    </span>
                  </div>
                ) : (
                  <a
                    href={RECRUITER_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold py-3.5 px-6 rounded-xl shadow-md shadow-pink-200/40 hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm text-center"
                  >
                    <Crown className="w-5 h-5" />
                    {t('common:recruiterCta.bookDemo')}
                  </a>
                )}
              </motion.div>
            </div>
          </div>
        </div>
        </div>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] border border-slate-200 p-5 sm:p-8 mb-8 sm:mb-10"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 text-center mb-5 sm:mb-8 tracking-tight">
            {t('comparison.title')}
          </h2>

          {/* Mobile: Toggle between Explorer / Recruiter */}
          <div className="flex md:hidden justify-center gap-2 mb-6">
            <button
              onClick={() => setTableView('explorer')}
              className={`px-4 py-2 rounded-full font-semibold text-sm transition-colors ${
                tableView === 'explorer'
                  ? 'bg-pink-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {t('comparison.explorerTab')}
            </button>
            <button
              onClick={() => setTableView('recruiter')}
              className={`px-4 py-2 rounded-full font-semibold text-sm transition-colors ${
                tableView === 'recruiter'
                  ? 'bg-pink-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {t('comparison.recruiterTab')}
            </button>
          </div>

          {/* Mobile: Single table based on toggle */}
          <div className="md:hidden">
            {tableView === 'explorer' && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[280px]">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="w-1/2 text-left py-3 px-3 font-semibold text-gray-700 text-sm">Feature</th>
                      <th className="w-1/4 text-center py-3 px-2 font-semibold text-gray-700 text-xs">Explorer Free</th>
                      <th className="w-1/4 text-center py-3 px-2 font-semibold text-gray-700 text-xs">Explorer Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {explorerFeatures.map((f, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-700 text-sm">{f.name}</span>
                            {f.info && (
                              <div className="group relative">
                                <Info className="w-4 h-4 text-gray-400 cursor-help flex-shrink-0" />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                                  {f.info}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={`py-3 px-2 text-center text-sm ${currentPlan === 'explorer_free' && isSignedIn ? 'bg-slate-50' : ''}`}>
                          {renderFeatureValue(f.explorer_free)}
                        </td>
                        <td className={`py-3 px-2 text-center text-sm ${currentPlan === 'explorer_premium' && isSignedIn ? 'bg-slate-50' : ''}`}>
                          {renderFeatureValue(f.explorer_premium)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tableView === 'recruiter' && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[280px]">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="w-1/2 text-left py-3 px-3 font-semibold text-gray-700 text-sm">Feature</th>
                      <th className="w-1/4 text-center py-3 px-2 font-semibold text-gray-700 text-xs">{t('comparison.sourcingCol')}</th>
                      <th className="w-1/4 text-center py-3 px-2 font-semibold text-gray-700 text-xs">{t('comparison.screeningCol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recruiterFeatures.map((f, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-700 text-sm">{f.name}</span>
                            {f.info && (
                              <div className="group relative">
                                <Info className="w-4 h-4 text-gray-400 cursor-help flex-shrink-0" />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                                  {f.info}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={`py-3 px-2 text-center text-sm ${currentPlan === 'recruiter_free' && isSignedIn ? 'bg-slate-50' : ''}`}>
                          {renderFeatureValue(f.recruiter_free)}
                        </td>
                        <td className={`py-3 px-2 text-center text-sm ${currentPlan === 'recruiter_star' && isSignedIn ? 'bg-slate-50' : ''}`}>
                          {renderFeatureValue(f.recruiter_premium)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Desktop: Both tables stacked */}
          <div className="hidden md:block space-y-8">
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4">{t('comparison.explorerTableTitle')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[400px]">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="w-1/3 text-left py-3 px-4 font-semibold text-gray-700">Feature</th>
                      <th className="w-1/3 text-center py-3 px-4 font-semibold text-gray-700">Explorer Free</th>
                      <th className="w-1/3 text-center py-3 px-4 font-semibold text-gray-700">Explorer Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {explorerFeatures.map((f, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-700">{f.name}</span>
                            {f.info && (
                              <div className="group relative">
                                <Info className="w-4 h-4 text-gray-400 cursor-help flex-shrink-0" />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                                  {f.info}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={`py-3 px-4 text-center ${currentPlan === 'explorer_free' && isSignedIn ? 'bg-slate-50' : ''}`}>
                          {renderFeatureValue(f.explorer_free)}
                        </td>
                        <td className={`py-3 px-4 text-center ${currentPlan === 'explorer_premium' && isSignedIn ? 'bg-slate-50' : ''}`}>
                          {renderFeatureValue(f.explorer_premium)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4">{t('comparison.recruiterTableTitle')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[400px]">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="w-1/3 text-left py-3 px-4 font-semibold text-gray-700">Feature</th>
                      <th className="w-1/3 text-center py-3 px-4 font-semibold text-gray-700">{t('comparison.sourcingCol')}</th>
                      <th className="w-1/3 text-center py-3 px-4 font-semibold text-gray-700">{t('comparison.screeningCol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recruiterFeatures.map((f, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-700">{f.name}</span>
                            {f.info && (
                              <div className="group relative">
                                <Info className="w-4 h-4 text-gray-400 cursor-help flex-shrink-0" />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 bg-gray-900 text-white text-xs rounded-lg p-2 z-10">
                                  {f.info}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={`py-3 px-4 text-center ${currentPlan === 'recruiter_free' && isSignedIn ? 'bg-slate-50' : ''}`}>
                          {renderFeatureValue(f.recruiter_free)}
                        </td>
                        <td className={`py-3 px-4 text-center ${currentPlan === 'recruiter_star' && isSignedIn ? 'bg-slate-50' : ''}`}>
                          {renderFeatureValue(f.recruiter_premium)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Payment Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <p className="text-slate-500 text-sm mb-3">
            {t('payment.title')}
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
            <Lock className="w-3.5 h-3.5" />
            <span>{t('payment.creditCard')}</span>
          </div>
        </motion.div>

        {/* FAQ / Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_16px_rgba(0,0,0,0.06)] p-5 sm:p-8"
        >
          <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-5 sm:mb-6 text-center tracking-tight">
            {t('faq.title')}
          </h3>
          <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
            {[
              { title: t('faq.billingCycleTitle'), text: t('faq.billingCycleText') },
              { title: t('faq.cancelTitle'), text: t('faq.cancelText') },
              { title: t('faq.secureTitle'), text: t('faq.secureText') },
              { title: t('faq.usageTitle'), text: t('faq.usageText') },
            ].map((item, i) => (
              <div key={i} className="p-4 rounded-xl bg-slate-50/80 border border-slate-100">
                <h4 className="font-semibold text-slate-800 text-sm mb-1.5">{item.title}</h4>
                <p className="text-[13px] text-slate-500 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default Pricing;

